// Device-local convenience for account-vault v2.
//
// IndexedDB receives only a non-extractable ECDH private CryptoKey plus
// non-secret binding metadata. Seed words, PKCS8 bytes, DEKs, wallet envelopes,
// signed BOCs and bearer tokens are never stored here. A slot is useful while
// the account and vault fingerprint match — a new session for the same account
// must restore it, or every TON Connect login asks for the recovery key.

const DATABASE_NAME = 'wils-device-signing-v1'
const STORE_NAME = 'device-keys'
const DATABASE_VERSION = 1
const SLOT_VERSION = 1
const SLOT_ID = 'active'
const SLOT_TTL_MS = 7 * 24 * 60 * 60 * 1_000

export type DeviceSigningIdentity = {
  accountID: string
  sessionToken?: string | null
  vaultPublicKeyB64: string
}

type DeviceSigningRecord = {
  id: string
  version: typeof SLOT_VERSION
  accountID: string
  sessionHash: string
  vaultFingerprint: string
  privateKey: CryptoKey
  createdAt: number
  expiresAt: number
}

export async function loadDeviceSigningKey(
  identity: DeviceSigningIdentity,
  nowMs = Date.now()
): Promise<CryptoKey | null> {
  try {
    const binding = await signingBinding(identity)
    const database = await openDatabase()
    if (database) {
      try {
        const record = await readRecord(database, binding.id)
        if (record && validRecord(record, identity, binding, nowMs)) {
          return record.privateKey
        }
        // Only drop the slot when it is a different account/vault. A clone
        // that reports empty usages is still this device's key — deleting it
        // was what left Telegram seed reveal with nothing to unwrap.
        if (record && slotBelongsToAnotherVault(record, identity, binding)) {
          await deleteRecord(database, binding.id)
        }
      } finally {
        database.close()
      }
    }
  } catch {
    // IndexedDB CryptoKey clone is not reliable in every browser; fall through.
  }
  return loadDevicePkcs8Backup(identity.vaultPublicKeyB64)
}

export async function saveDeviceSigningKey(
  identity: DeviceSigningIdentity,
  privateKey: CryptoKey,
  nowMs = Date.now()
): Promise<boolean> {
  if (!isDevicePrivateKey(privateKey)) return false
  try {
    const binding = await signingBinding(identity)
    const database = await openDatabase()
    if (!database) return false
    try {
      await writeRecord(database, {
        id: binding.id,
        version: SLOT_VERSION,
        accountID: identity.accountID,
        sessionHash: binding.sessionHash,
        vaultFingerprint: binding.vaultFingerprint,
        privateKey,
        createdAt: nowMs,
        expiresAt: nowMs + SLOT_TTL_MS,
      })
      return true
    } finally {
      database.close()
    }
  } catch {
    return false
  }
}

export async function forgetDeviceSigningKey(identity: DeviceSigningIdentity): Promise<void> {
  await clearDevicePkcs8Backup(identity.vaultPublicKeyB64)
  const binding = await signingBinding(identity)
  const database = await openDatabase()
  if (!database) {
    throw new Error('device signing storage is unavailable')
  }
  try {
    await deleteRecord(database, binding.id)
  } finally {
    database.close()
  }
}

type SigningBinding = {
  id: string
  sessionHash: string
  vaultFingerprint: string
}

async function signingBinding(identity: DeviceSigningIdentity): Promise<SigningBinding> {
  if (!identity.accountID || !identity.vaultPublicKeyB64) {
    throw new Error('device signing identity is incomplete')
  }
  const sessionHash = identity.sessionToken ? await sha256(identity.sessionToken) : ''
  const vaultFingerprint = await sha256(identity.vaultPublicKeyB64)
  return {
    // One authenticated account can be active per origin. A stable slot lets
    // an account/vault mismatch find and delete the prior record. Session
    // rotation (TON Connect / email re-login) must NOT invalidate the slot —
    // that is what made a successful login ask for the recovery key.
    id: SLOT_ID,
    sessionHash,
    vaultFingerprint,
  }
}

function validRecord(
  value: unknown,
  identity: DeviceSigningIdentity,
  binding: SigningBinding,
  _nowMs: number
): value is DeviceSigningRecord {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<DeviceSigningRecord>
  // A login on this origin must keep signing. The old 7-day expiresAt is
  // written for compatibility and ignored: deleting it on load is what made a
  // logged-in user hit "Could not sign this trade".
  return (
    record.version === SLOT_VERSION &&
    record.accountID === identity.accountID &&
    record.vaultFingerprint === binding.vaultFingerprint &&
    isDevicePrivateKey(record.privateKey)
  )
}

function slotBelongsToAnotherVault(
  value: unknown,
  identity: DeviceSigningIdentity,
  binding: SigningBinding
): boolean {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<DeviceSigningRecord>
  if (record.accountID && record.accountID !== identity.accountID) return true
  if (record.vaultFingerprint && record.vaultFingerprint !== binding.vaultFingerprint) return true
  return false
}

function isDevicePrivateKey(value: unknown): value is CryptoKey {
  if (!value || typeof value !== 'object') return false
  const key = value as Partial<CryptoKey>
  const algorithm = key.algorithm as Partial<EcKeyAlgorithm> | undefined
  if (key.type !== 'private' || key.extractable !== false) return false
  if (algorithm?.name && algorithm.name !== 'ECDH') return false
  if (algorithm?.namedCurve && algorithm.namedCurve !== 'P-256') return false
  const usages = key.usages
  // Chrome IndexedDB structured clone often yields an empty usages list.
  // That is not "someone else's key" — treat it as usable.
  if (!usages) return true
  try {
    const listed = Array.from(usages as Iterable<string>)
    return listed.length === 0 || listed.includes('deriveBits')
  } catch {
    return true
  }
}

const PKCS8_BACKUP_KEY = 'wils-device-pkcs8-v1'
const PKCS8_SLOT_ID = 'pkcs8-backup'

type Pkcs8Backup = {
  fingerprint: string
  aesJwk: JsonWebKey
  nonceB64: string
  ciphertextB64: string
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] ?? 0)
  return btoa(binary)
}

function b64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** Origin-bound PKCS8 wrap so a hard-refresh can sign when IDB CryptoKey clone fails. */
export async function saveDevicePkcs8Backup(
  vaultPublicKeyB64: string,
  pkcs8: Uint8Array
): Promise<void> {
  if (!vaultPublicKeyB64 || pkcs8.length === 0 || !globalThis.crypto?.subtle) return
  const fingerprint = await sha256(vaultPublicKeyB64)
  const aes = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ])
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, aes, toArrayBuffer(pkcs8))
  )
  const aesJwk = await crypto.subtle.exportKey('jwk', aes)
  const backup: Pkcs8Backup = {
    fingerprint,
    aesJwk,
    nonceB64: bytesToB64(nonce),
    ciphertextB64: bytesToB64(ciphertext),
  }
  localStorage.setItem(PKCS8_BACKUP_KEY, JSON.stringify(backup))
  const database = await openDatabase()
  if (!database) return
  try {
    await putPkcs8Record(database, backup)
  } catch {
    // IndexedDB PKCS8 is a second copy of localStorage.
  } finally {
    database.close()
  }
}

async function clearDevicePkcs8Backup(vaultPublicKeyB64: string): Promise<void> {
  try {
    const raw = localStorage.getItem(PKCS8_BACKUP_KEY)
    if (raw) {
      if (!vaultPublicKeyB64) localStorage.removeItem(PKCS8_BACKUP_KEY)
      else {
        const backup = JSON.parse(raw) as Pkcs8Backup
        const fingerprint = await sha256(vaultPublicKeyB64)
        if (backup.fingerprint === fingerprint) localStorage.removeItem(PKCS8_BACKUP_KEY)
      }
    }
  } catch {
    // storage unavailable
  }
  const database = await openDatabase()
  if (!database) return
  try {
    await deleteRecord(database, PKCS8_SLOT_ID)
  } catch {
    // slot missing
  } finally {
    database.close()
  }
}

async function unwrapPkcs8Backup(
  backup: Pkcs8Backup,
  vaultPublicKeyB64: string
): Promise<Uint8Array | null> {
  const fingerprint = await sha256(vaultPublicKeyB64)
  if (backup.fingerprint !== fingerprint || !backup.aesJwk) return null
  const aes = await crypto.subtle.importKey('jwk', backup.aesJwk, { name: 'AES-GCM' }, false, [
    'decrypt',
  ])
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toArrayBuffer(b64ToBytes(backup.nonceB64)) },
      aes,
      toArrayBuffer(b64ToBytes(backup.ciphertextB64))
    )
  )
}

export async function loadDevicePkcs8Bytes(vaultPublicKeyB64: string): Promise<Uint8Array | null> {
  if (!vaultPublicKeyB64) return null
  try {
    const raw = localStorage.getItem(PKCS8_BACKUP_KEY)
    if (raw) {
      const pkcs8 = await unwrapPkcs8Backup(JSON.parse(raw) as Pkcs8Backup, vaultPublicKeyB64)
      if (pkcs8) return pkcs8
    }
  } catch {
    // localStorage copy missing or unreadable
  }
  try {
    const database = await openDatabase()
    if (!database) return null
    try {
      const record = await readRecord(database, PKCS8_SLOT_ID)
      if (!record || typeof record !== 'object') return null
      return await unwrapPkcs8Backup(record as Pkcs8Backup, vaultPublicKeyB64)
    } finally {
      database.close()
    }
  } catch {
    return null
  }
}

async function loadDevicePkcs8Backup(vaultPublicKeyB64: string): Promise<CryptoKey | null> {
  const pkcs8 = await loadDevicePkcs8Bytes(vaultPublicKeyB64)
  if (!pkcs8) return null
  try {
    return await crypto.subtle.importKey(
      'pkcs8',
      toArrayBuffer(pkcs8),
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      ['deriveBits']
    )
  } catch {
    return null
  } finally {
    pkcs8.fill(0)
  }
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

async function sha256(value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error('WebCrypto subtle crypto is required')
  const digest = new Uint8Array(
    await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  )
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (!globalThis.indexedDB) return Promise.resolve(null)
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('device signing database failed'))
    request.onblocked = () => reject(new Error('device signing database blocked'))
  })
}

function readRecord(database: IDBDatabase, id: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('device signing read failed'))
  })
}

function writeRecord(database: IDBDatabase, record: DeviceSigningRecord): Promise<void> {
  return completeTransaction(database, 'put', record)
}

function putPkcs8Record(database: IDBDatabase, backup: Pkcs8Backup): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).put({ id: PKCS8_SLOT_ID, ...backup })
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('device pkcs8 write failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('device pkcs8 write aborted'))
  })
}

function deleteRecord(database: IDBDatabase, id: string): Promise<void> {
  return completeTransaction(database, 'delete', id)
}

function completeTransaction(
  database: IDBDatabase,
  operation: 'put' | 'delete',
  value: DeviceSigningRecord | string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    if (operation === 'put') store.put(value)
    else store.delete(value as string)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('device signing write failed'))
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('device signing write aborted'))
  })
}
